import { createSelector } from 'reselect'
import { selectCurrencySymbol } from '../system'
import { calcCartItemTotalPrice, getDiscountInCurrency, getPercentDiscountInCurrency, roundPrice } from '../../../helper'
import { getGiftCouponsPayment } from '../../../helper/getGiftCouponsPayment'
import { COUPON_TYPES } from './constants'
import { compareCartItems, convert2CashDeskResultItem, isItemPriceDisabled } from './helper/cashdesk'
import { convert2ResultItems, convertToClientMonitorPurchaseItems, sortItemsByLastCoupon } from './helper'

export const selectDeliveryCartItems = store => store.deliveryCart.items
export const selectCashDeskItems = store => store.cashDesk.items
const selectCashDeskOrderType = store => store.cashDesk.orderType

export const selectCartInitialDiscount = state => state.cartDiscount.initialDiscount?.discount

export const selectCartDiscount = state => state.cartDiscount?.discount

export const selectCartDiscountType = state => state.cartDiscount?.discountType

export const selectTableCartOrders = store => store.tableCart.orders
export const selectTableSplits = store => store.restaurant.table?.splits
export const selectTableCartNewOrder = store => store.tableCart?.newOrder

export const selectCartExtraDiscount = store => store.cartDiscount?.extraDiscount
export const selectCartExtraDiscountType = store => store.cartDiscount?.extraDiscountType

export const selectCurrentDeliveryCost = store => store.deliveryCart?.currentDeliveryCost
export const selectDeliveryCartPeriodConfig = store => store.deliveryCart.periodConfig

const selectCashDeskGiftCoupons = store => store.cashDesk.giftCoupons
const selectDeliveryCartGiftCoupons = store => store.deliveryCart.giftCoupons
const selectPaymentGiftCoupons = store => store.cartPayment?.giftCoupons

export const selectCouponInCart = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	(deliveryCartItems, cashDeskItems) => {
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		return items?.find(item => item.type === 'MARKETING_COUPON' && item.status !== 'CANCELED') || null
	},
)

export const selectStampVoucherInCart = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	(deliveryCartItems, cashDeskItems) => {
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		return items?.find(item => item.type === 'STAMP_VOUCHER' && item.status !== 'CANCELED') || null
	},
)

export const selectResultItemsInCart = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectResultItemsInCashDeskCart,
	// TODO selectRestaurantItems
	selectCurrencySymbol,
	(deliveryCartItems, cashDeskResultItems, currencySymbol) => {
		return cartType === 'DELIVERY' ? convert2ResultItems(deliveryCartItems, currencySymbol) : cashDeskResultItems
	},
)

export const selectResultItemsInCashDeskCart = createSelector(
	selectCashDeskItems,
	selectCashDeskOrderType,
	selectCurrencySymbol,
	(cashDeskItems, orderType, currencySymbol) => {
		return cashDeskItems.map(cashDeskItem => convert2CashDeskResultItem(cashDeskItem, orderType, currencySymbol)).sort(sortItemsByLastCoupon)
	},
)

export const selectGiftCouponInCart = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	(deliveryCartItems, cashDeskItems) => {
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		return items?.find(item => item.type === 'GIFT_COUPON') || null
	},
)

export const selectHasAnyItemsInCart = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	selectTableCartOrders,
	selectTableSplits,
	selectTableCartNewOrder,
	(deliveryCartItems, cashDeskItems, orders, splits, newOrder) => {
		if (cartType === 'DELIVERY') {
			return !!deliveryCartItems.length
		} else if (cartType === 'CASH_DESK') {
			return !!cashDeskItems.length

		} else {
			return !!orders?.length || !!splits?.length || newOrder
		}
	},
)

export const selectCartItemsTotal = (cartType) => createSelector(
	selectResultItemsInCart(cartType),
	(resultItems) => {
		return resultItems?.reduce((tp, i) => tp + calcCartItemTotalPrice(i), 0)
	},
)

export const selectCartItemsTotalForCoupon = (cartType) => createSelector(
	selectResultItemsInCart(cartType),
	(resultItems) => {
		return resultItems?.reduce((tp, i) => {
			const hasDiscountWithoutOtherDiscounts = !i?.disabledDiscount && i?.discountInfo && !i?.discountInfo?.enabledWithOtherDiscounts
			if (hasDiscountWithoutOtherDiscounts) return tp
			return tp + calcCartItemTotalPrice(i)
		}, 0)
	}
)

export const selectCartItemsTotalWithDiscount = (cartType) => createSelector(
	selectResultItemsInCart(cartType),
	(resultItems) => {
		return resultItems?.reduce((tp, i) => tp + calcCartItemTotalPrice(i, true), 0)
	},
)

export const selectCartCategoryDiscountInCurrency = (cartType, itemsWithOtherEnabledDiscount) => createSelector(
	selectResultItemsInCart(cartType),
	(resultItems) => {
		return resultItems?.reduce((totalDiscount, item) => {
			if (item?.disabledDiscount || !item?.discountInfo) return totalDiscount
			if (itemsWithOtherEnabledDiscount && !item?.discountInfo?.enabledWithOtherDiscounts) return totalDiscount
			const totalPrice = calcCartItemTotalPrice(item)
			const discount = getDiscountInCurrency(item?.discountInfo?.value, item?.discountInfo?.type, totalPrice)
			return totalDiscount + Math.min(discount, totalPrice)
		}, 0)
	}
)

export const selectCartDiscountInCurrency = (cartType) => createSelector(
	selectGiftCouponInCart(cartType),
	selectCartDiscount,
	selectCartItemsTotalWithDiscount(cartType),
	selectMoneyExchangeInCart(cartType),
	(hasGiftCoupon, discount, itemsTotalWithDiscount, hasMoneyExchange) => {
		const discountValue = (hasGiftCoupon || hasMoneyExchange) ? 0 : discount
		return getPercentDiscountInCurrency(discountValue, itemsTotalWithDiscount)
	},
)

export const selectExtraDiscountInCurrency = (cartType) => createSelector(
	selectCartExtraDiscount,
	selectCartExtraDiscountType,
	selectCartItemsTotal(cartType),
	selectCartDiscountInCurrency(cartType),
	selectGiftCouponInCart(cartType),
	selectMoneyExchangeInCart(cartType),
	selectCartCategoryDiscountInCurrency(cartType),
	(extraDiscount, extraDiscountType, itemsTotal, discountInCurrency, hasGiftCoupon, hasMoneyExchange, categoryDiscountInCurrency) => {
		const price = itemsTotal - categoryDiscountInCurrency - discountInCurrency
		const discountValue = (hasGiftCoupon || hasMoneyExchange) ? 0 : extraDiscount
		return getDiscountInCurrency(discountValue, extraDiscountType, price)
	},
)

export const selectCouponDiscountInCurrency = (cartType) => createSelector(
	selectCouponInCart(cartType),
	selectCartItemsTotalForCoupon(cartType),
	selectCartDiscountInCurrency(cartType),
	selectExtraDiscountInCurrency(cartType),
	selectCartCategoryDiscountInCurrency(cartType, true),
	(coupon, itemsTotal, discountInCurrency, extraDiscountInCurrency, categoryDiscountInCurrency) => {
		const price = itemsTotal - categoryDiscountInCurrency - discountInCurrency - extraDiscountInCurrency
		return getDiscountInCurrency(coupon?.initialProduct?.discountData?.discount,
			coupon?.initialProduct?.discountData?.discountType, price)
	},
)

export const selectStampVoucherDiscountInCurrency = (cartType) => createSelector(
	selectStampVoucherInCart(cartType),
	(stampVoucher) => {
		return Math.abs(stampVoucher?.price) || 0
	},
)

export const selectCartTotalPrice = (cartType) => createSelector(
	selectCartItemsTotal(cartType),
	selectCartDiscountInCurrency(cartType),
	selectExtraDiscountInCurrency(cartType),
	selectCouponDiscountInCurrency(cartType),
	selectStampVoucherDiscountInCurrency(cartType),
	selectCartCategoryDiscountInCurrency(cartType),
	(itemsTotal, discountInCurrency, extraDiscountInCurrency, couponDiscountInCurrency, stampVoucherDiscountInCurrency, categoryDiscountInCurrency) => {
		return roundPrice(itemsTotal - categoryDiscountInCurrency - discountInCurrency - extraDiscountInCurrency - couponDiscountInCurrency - stampVoucherDiscountInCurrency)
	},
)

export const selectCartTotalPriceForCoupon = (cartType) => createSelector(
	selectCartItemsTotalForCoupon(cartType),
	selectCartDiscountInCurrency(cartType),
	selectExtraDiscountInCurrency(cartType),
	selectCouponDiscountInCurrency(cartType),
	selectStampVoucherDiscountInCurrency(cartType),
	selectCartCategoryDiscountInCurrency(cartType, true),
	(itemsTotal, discountInCurrency, extraDiscountInCurrency, couponDiscountInCurrency, stampVoucherDiscountInCurrency, categoryDiscountInCurrency) => {
		return roundPrice(itemsTotal - categoryDiscountInCurrency - discountInCurrency - extraDiscountInCurrency - couponDiscountInCurrency - stampVoucherDiscountInCurrency)
	}
)

export const selectCartTotalPriceWithGiftCoupons = (cartType, giftCouponSum = 0) => createSelector(
	selectCartTotalPrice(cartType),
	(totalPrice) => {
		return roundPrice(totalPrice - giftCouponSum)
	},
)

export const selectDiscountForItem = (price, item) => createSelector(
	selectCartDiscount,
	(discount) => {
		const hasCategoryDiscount = !!item?.discountInfo
		if (!hasCategoryDiscount) {
			return getPercentDiscountInCurrency(discount, price)
		} else {
			const categoryDiscount = getDiscountInCurrency(item?.discountInfo?.value, item?.discountInfo?.type, price)
			if (price <= categoryDiscount) return price
			if (item?.discountInfo?.enabledWithOtherDiscounts) {
				const discountPrice = getPercentDiscountInCurrency(discount, price)
				return roundPrice(categoryDiscount + discountPrice)
			} else {
				return categoryDiscount
			}
		}
	}
)

export const selectDeliveryCost = (cartType) => createSelector(
	selectCurrentDeliveryCost,
	(deliveryCost) => {
		return cartType === 'DELIVERY' ? deliveryCost : 0
	},
)

export const selectGiftCoupons = cartType => createSelector(
	selectCashDeskGiftCoupons,
	selectDeliveryCartGiftCoupons,
	selectPaymentGiftCoupons,
	(cashDeskGiftCoupons, deliveryCartGiftCoupons, paymentGiftCoupons) => {
		if (cartType === 'DELIVERY') {
			return deliveryCartGiftCoupons
		} else if (cartType === 'CART_PAYMENT') {
			return paymentGiftCoupons
		} else {
			return cashDeskGiftCoupons
		}
	},
)

export const selectClientMonitorItems = (cartType, desktopDeviceId) => createSelector(
	selectResultItemsInCart(cartType),
	selectCartDiscount,
	selectCartDiscountType,
	selectCartDiscountInCurrency(cartType),
	selectCartExtraDiscount,
	selectCartExtraDiscountType,
	selectExtraDiscountInCurrency(cartType),
	selectDeliveryCost(cartType),
	selectCouponDiscountInCurrency(cartType),
	selectGiftCoupons(cartType),
	(
		resultItems,
		discountValue, discountType, discountInCurrency,
		extraDiscountValue, extraDiscountType, extraDiscountInCurrency,
		deliveryCost,
		couponDiscountInCurrency, giftCoupons,
	) => {
		return {
			desktopDeviceId,
			purchaseItems: convertToClientMonitorPurchaseItems(resultItems, discountValue, discountType, discountInCurrency, extraDiscountValue, extraDiscountType, extraDiscountInCurrency, deliveryCost, couponDiscountInCurrency),
			payments: getGiftCouponsPayment(giftCoupons),
		}
	},
)

export const selectGiftCouponSum = (cartType) => createSelector(
	selectGiftCoupons(cartType),
	(giftCoupons) => {
		return giftCoupons?.length ? giftCoupons?.reduce((res, el) => res + el.cartBalance, 0) : 0
	},
)

export const selectMoneyExchangeInCart = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	(deliveryCartItems, cashDeskItems) => {
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		return !!items?.find(item => item.type === 'CASH_EXCHANGE')
	},
)

export const selectCategoryDiscountWithoutDiscounts = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	(deliveryCartItems, cashDeskItems) => {
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		return !!items?.length && items.every(item => !item?.initialProduct?.disabledDiscount && item?.discountInfo && !item?.discountInfo?.enabledWithOtherDiscounts)
	}
)

const selectDeliveryCartOrder = store => store.deliveryCart.order
const selectCashDeskOrder = store => store.cashDesk.order

export const selectOrder = (cartType) => createSelector(
	selectDeliveryCartOrder,
	selectCashDeskOrder,
	(deliveryCartOrder, cashDeskOrder) => {
		return cartType === 'DELIVERY' ? deliveryCartOrder : cashDeskOrder
	},
)
export const selectHasUnpaidOrderInCart = (cartType) => createSelector(
	selectDeliveryCartOrder,
	selectCashDeskOrder,
	(deliveryCartOrder, cashDeskOrder) => {
		const order = cartType === 'DELIVERY' ? deliveryCartOrder : cashDeskOrder
		return order?.payments.some(payment => payment.payed === false)
	},
)

export const selectNewItemsUnpaidOrder = (cartType) => createSelector(
	selectDeliveryCartItems,
	selectCashDeskItems,
	(deliveryCartItems, cashDeskItems) => {
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		return items?.filter(item => !item.itemPositionId)
	},
)

export const selectIsChangedUnpaidOrder = (cartType) => createSelector(
	selectHasUnpaidOrderInCart(cartType),
	selectDeliveryCartItems,
	selectCashDeskItems,
	(hasUnpaidOrder, deliveryCartItems, cashDeskItems) => {
		if (!hasUnpaidOrder) {
			return
		}
		const items = cartType === 'DELIVERY' ? deliveryCartItems : cashDeskItems
		const isNewItems = items?.filter(item => !COUPON_TYPES.includes(item.type)).some(item => !item.itemPositionId)
		const isCanceledItems = items?.some(item => item.status === 'CANCELED')
		const isCanceledSubItems = items.some(item => item.items && item.items.some(subItem => subItem.status === 'CANCELED'))

		return isNewItems || isCanceledItems || isCanceledSubItems
	},
)

export const selectDiscountUnpaidOrder = (cartType) => createSelector(
	selectOrder(cartType),
	(order) => {
		if (order?.payments.some(payment => payment.payed === false)) {
			if (!order?.totalDiscount) {
				return 0
			}
			const categoryDiscountOwnerIds = new Set((order?.purchaseItems || [])
				.filter(item => item?.type === 'CATEGORY_DISCOUNT')
				.map(item => item?.targetItemPositionId))
			const itemsTotalWithDiscount = (order?.items || [])
				.filter(item => !['MARKETING_COUPON', 'EXTRA_DISCOUNT', 'STAMP_VOUCHER'].includes(item?.type))
				.filter(item => !categoryDiscountOwnerIds.has(item?.itemPositionId))
				.reduce((tp, i) => tp + calcCartItemTotalPrice(i), 0)
			if (!itemsTotalWithDiscount) {
				return 0
			}
			return roundPrice((order?.totalDiscount * 100) / itemsTotalWithDiscount)
		} else {
			return null
		}
	},
)

const selectCashDeskOnlyProductItems = createSelector(
	selectCashDeskItems,
	(items) => {
		return items?.filter(item => !COUPON_TYPES.includes(item.type))
	},
)

export const selectCashDeskIsExistDisabledPriceItems = createSelector(
	selectCashDeskOnlyProductItems,
	selectCashDeskOrderType,
	(items, orderType) => {
		const disabledPriceItems = items?.reduce((acc, item) => {
			if (isItemPriceDisabled(item, orderType)) {
				acc.push(item)
			}
			return acc
		}, [])
		return disabledPriceItems.length > 0
	})

export const selectCashDeskCartItems = createSelector(
	selectCashDeskItems,
	selectCashDeskIsExistDisabledPriceItems,
	selectCashDeskOrderType,
	(items, isExistDisabledPriceItems, orderType) => {
		const cartItems = items.map((item, i) => ({ ...item, initialIndex: i }))
		if (!isExistDisabledPriceItems) {
			return cartItems
		} else {
			return cartItems.reduce((res, item) => {
				const disabled = isItemPriceDisabled(item, orderType)
				return [...res, { ...item, disabled }]
			}, []).sort(compareCartItems)
		}
	},
)

export const selectCashDeskBeeperConfigs = store => store.cashDeskSettings.beeperConfigs?.beeperRequired
export const selectDeliveryBeeperConfigs = store => store.deliverySettings.beeperConfigs?.beeperRequired

export const selectBeeperRequired = (cartType) => createSelector(
	selectCashDeskBeeperConfigs,
	selectDeliveryBeeperConfigs,
	(cashDeskBeeperConfigs, deliveryBeeperConfigs) => {
		return cartType === 'DELIVERY' ? deliveryBeeperConfigs : cashDeskBeeperConfigs
	},
)
